import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RequestQuoteOutlined from '@mui/icons-material/RequestQuoteOutlined';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Divider,
    InputAdornment,
    TextField,
    Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Organisation from 'shared/db/services/Organisation';
import * as yup from 'yup';

import { BackLinkButton } from '@/components';
import { useImagesContext } from '@/context/ImagesContext';
import { useUserContext } from '@/context/UserContext';
import { useDatabase } from '@/hooks';
import { useOrganisationData } from '@/hooks/useOrganisationData';
import { ROUTE } from '@/router/routes';
import { databaseValidation } from '@/services/databaseValidation';
import Logger from '@/services/logger';
import { Snackbar } from '@/services/toastNotifications';

type FormValues = {
    taxRate: string;
    productsTaxRate: string;
};

const PATTERN_TWO_DIGITS_AFTER_COMMA = /^\d+(\.\d{0,2})?$/;

function getValidationSchema(isProductsTaxRateRequired: boolean) {
    return yup.object().shape({
        taxRate: yup
            .string()
            .test('is-decimal', 'Invoicing:invalid_tax_rate', (val) => {
                if (val) {
                    const transformed = val.replace(',', '.');
                    return PATTERN_TWO_DIGITS_AFTER_COMMA.test(transformed);
                }
                return true;
            })
            .test(
                'max-value',
                'Invoicing:sales_taxes_error:max_value',
                (val) => {
                    if (val) {
                        const transformed = val.replace(',', '.');
                        return Number(transformed) <= 100;
                    }
                    return true;
                },
            ),
        productsTaxRate: yup
            .string()
            .test('required', 'Invoicing:sales_taxes_error:required', (val) =>
                isProductsTaxRateRequired ? Boolean(val) : true,
            )
            .test('is-decimal', 'Invoicing:invalid_tax_rate', (val) => {
                if (val && isProductsTaxRateRequired) {
                    const transformed = val.replace(',', '.');
                    return PATTERN_TWO_DIGITS_AFTER_COMMA.test(transformed);
                }
                return true;
            })
            .test(
                'max-value',
                'Invoicing:sales_taxes_error:max_value',
                (val) => {
                    if (!isProductsTaxRateRequired) {
                        return true;
                    }
                    const transformed = val?.replace(',', '.') || '';
                    return Number(transformed) <= 100;
                },
            ),
    });
}

function SalesTaxesPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { getDatabase } = useDatabase();
    const { ImagesService } = useImagesContext();
    const { userProfileData } = useUserContext();
    const { organisation, isLoading } = useOrganisationData();

    const [isProductsRateVisible, setIsProductsRateVisible] = useState<
        boolean | null
    >(null);

    const showProductsRate =
        isProductsRateVisible ?? !!organisation?.productsTaxRate;

    const organisationService = useMemo(
        () =>
            new Organisation({
                database: getDatabase(),
                imageService: ImagesService,
                logDBAction: Logger.logRecordActivity,
                validation: databaseValidation,
            }),
        [getDatabase, ImagesService],
    );

    const validationSchema = useMemo(
        () => getValidationSchema(showProductsRate),
        [showProductsRate],
    );

    const handleFormSubmit = useCallback(
        async (values: FormValues) => {
            try {
                const formattedTaxRate = values.taxRate?.replace(',', '.');
                const formattedProductsTaxRate =
                    values.productsTaxRate?.replace(',', '.');

                await organisationService.update({
                    payload: {
                        taxRate: formattedTaxRate,
                        productsTaxRate: showProductsRate
                            ? formattedProductsTaxRate
                            : '',
                        accountingProvider: 'internal',
                    },
                    userId: userProfileData?.id || '',
                });

                navigate(ROUTE.settings);
            } catch {
                Snackbar.showToastNotification({
                    message: t('App:Messages:something_went_wrong'),
                    options: { variant: 'error' },
                });
            }
        },
        [
            organisationService,
            showProductsRate,
            userProfileData?.id,
            navigate,
            t,
        ],
    );

    const formik = useFormik<FormValues>({
        enableReinitialize: true,
        initialValues: {
            taxRate: organisation?.taxRate || '',
            productsTaxRate: organisation?.productsTaxRate || '',
        },
        validationSchema,
        onSubmit: handleFormSubmit,
    });

    const handleRemoveProductsRate = useCallback(() => {
        setIsProductsRateVisible(false);
        formik.setFieldValue('productsTaxRate', '');
    }, [formik]);

    const handleAddProductsRate = useCallback(() => {
        setIsProductsRateVisible(true);
    }, []);

    if (isLoading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '50vh',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ pb: 4 }}>
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 4,
                }}
            >
                <BackLinkButton
                    to={ROUTE.settings}
                    text="Settings"
                    testID="SalesTaxesPage-BackButton"
                />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <RequestQuoteOutlined fontSize="small" />
                <Typography fontSize={16} fontWeight="bold">
                    {t('Settings:invoices_admin:sales_taxes_header')}
                </Typography>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Box sx={{ maxWidth: 500 }}>
                <Alert severity="info" sx={{ mb: 4 }}>
                    {t('Invoicing:sales_taxes_info_title')}{' '}
                    {t('Invoicing:sales_taxes_info')}
                </Alert>

                <form onSubmit={formik.handleSubmit}>
                    {/* Default Sales Tax Rate */}
                    <Typography fontWeight="bold" fontSize={16} sx={{ mb: 1 }}>
                        {t('Invoicing:default_sales_tax')}
                    </Typography>
                    <Typography
                        fontSize={14}
                        color="text.secondary"
                        sx={{ mb: 2 }}
                    >
                        {t('Invoicing:default_sales_tax_info')}
                    </Typography>
                    <TextField
                        name="taxRate"
                        label={t('Invoicing:default_sales_tax')}
                        value={formik.values.taxRate}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                            formik.touched.taxRate && !!formik.errors.taxRate
                        }
                        helperText={
                            formik.touched.taxRate && formik.errors.taxRate
                                ? t(formik.errors.taxRate)
                                : t('Invoicing:default_sales_tax_note')
                        }
                        inputProps={{ inputMode: 'decimal' }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    %
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 4, width: 220 }}
                        data-test-id="SalesTaxesPage-TaxRateField"
                    />

                    {/* Default Sales Tax Rate for Products */}
                    <Typography fontWeight="bold" fontSize={16} sx={{ mb: 1 }}>
                        {t('Invoicing:default_products_sales_tax')}
                    </Typography>
                    <Typography
                        fontSize={14}
                        color="text.secondary"
                        sx={{ mb: 2 }}
                    >
                        {t('Invoicing:default_products_sales_tax_info')}
                    </Typography>

                    {showProductsRate ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 2,
                                mb: 4,
                            }}
                        >
                            <TextField
                                name="productsTaxRate"
                                label={t('Invoicing:default_sales_tax')}
                                value={formik.values.productsTaxRate}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={
                                    formik.touched.productsTaxRate &&
                                    !!formik.errors.productsTaxRate
                                }
                                helperText={
                                    formik.touched.productsTaxRate &&
                                    formik.errors.productsTaxRate
                                        ? t(formik.errors.productsTaxRate)
                                        : undefined
                                }
                                inputProps={{ inputMode: 'decimal' }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            %
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ width: 220 }}
                                data-test-id="SalesTaxesPage-ProductsTaxRateField"
                            />
                            <Button
                                color="error"
                                startIcon={<DeleteOutlineIcon />}
                                onClick={handleRemoveProductsRate}
                                sx={{ mt: 1, textTransform: 'none' }}
                                data-test-id="SalesTaxesPage-RemoveProductsRateButton"
                            >
                                {t('Actions:remove')}
                            </Button>
                        </Box>
                    ) : (
                        <Button
                            variant="outlined"
                            startIcon={<AddCircleOutlineIcon />}
                            onClick={handleAddProductsRate}
                            sx={{ mb: 4, textTransform: 'none' }}
                            data-test-id="SalesTaxesPage-AddProductsRateButton"
                        >
                            {t('InvoiceSystem:business_details:actions:add')}
                        </Button>
                    )}

                    {/* Save Button */}
                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        size="large"
                        disabled={formik.isSubmitting || !formik.dirty}
                        data-test-id="SalesTaxesPage-SaveButton"
                    >
                        {formik.isSubmitting ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            t('Actions:save_changes')
                        )}
                    </Button>
                </form>
            </Box>
        </Box>
    );
}

export default SalesTaxesPage;
